// Gegner im Feld — deterministisch, aus einer **eigenen** Ziehungsfolge.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/hostiles.py`.**
//
// Der entscheidende Punkt: die Folge hat mit der Brockenfolge nichts zu tun.
// Eine Ziehung dort einzufuegen verschoebe jede folgende, und weil Client und
// Server das Feld getrennt ausrechnen, saehen sie danach verschiedene Welten —
// ohne dass irgendwo ein Fehler auftaucht (Gates FG-20, FG-30). Mit der eigenen
// Folge bleibt der Golden Vector der Brocken unveraendert gueltig.
//
// IDs liegen in einem eigenen Raum (`e:cx:cy:i`), damit ein Gegner nie mit
// einem Brocken verwechselt werden kann.

import { balancing, enemy, zoneAtDist, zonesFor } from '../data/balancing';
import type { Enemy } from '../data/types';
import { TAU, jsRound } from '../core/math';
import { hintOf } from '../core/format';
import { hash3, rngFrom } from '../core/rng';
import { eraSeed } from './field';
import { pickVictim, suppression } from './wardenfire';

/** Sekunden, bis ein erledigter Gegner nachrueckt — Spiegel von `HOSTILE_RESPAWN`. */
const HOSTILE_RESPAWN = 90;

/** „Noch nie getroffen" — liegt beliebig weit in der Vergangenheit. */
const NEVER = -Infinity;

/**
 * Was ein Schuss gegen einen **Gegner** ausrichtet.
 *
 * Wortgleiches Gegenstueck zu `_aim_hostiles` auf dem Server: der Zuschlag
 * `ENEMY_DAMAGE_MUL` haengt an der **Zielrechnung**, nicht am Buchen. Wie viele
 * Zielplaetze ein Gegner braucht, haengt am tatsaechlichen Schaden je Platz —
 * ein Zuschlag erst in `HostileField.damage` waere online doppelt, weil der
 * Server ihn schon angewendet hat, bevor die Abweichung ankommt.
 */
export function enemyDamage(raw: number): number {
  return raw * balancing().enemyDamageMul;
}

/**
 * Wie viele Zielplaetze dieser Gegner **noch** braucht — nie mehr als noetig.
 *
 * **Buendeln statt streuen.** Vorher bekam jeder Gegner genau einen Platz je
 * Kanal: bei vier Zielplaetzen und vier Gegnern also jeder ein Viertel der
 * Feuerkraft — alle vier ueberlebten lange, und alle vier schossen die ganze
 * Zeit weiter auf die Huelle.
 *
 * Overkill ist die andere Verschwendung: der Platz auf einem Gegner, der in
 * diesem Zyklus ohnehin faellt, fehlt dem naechsten. Spiegel von `_slots_for`.
 */
export function slotsFor(rest: number, perSlot: number, free: number): number {
  if (free <= 0 || perSlot <= 0 || rest <= 0) return 0;
  return Math.min(free, Math.ceil(rest / perSlot));
}

/**
 * Ist diese Art ein **Koloss** — der Koop-Inhalt, kein gewöhnlicher Wächter?
 *
 * Eine Tabellenfrage, kein ID-Vergleich: `swarmmother`, `archivist` und der
 * Wächter des tiefen Raums sind dieselbe Sorte Inhalt, und eine vierte käme
 * durch eine Tabellenzeile dazu. Sie steht hier und nicht bei den Aufrufern,
 * weil `Ship` die Gegnertabelle sonst selbst lesen müsste.
 *
 * Ein unbekannter Name ist **kein** Koloss — die Tabellen kommen vom Server,
 * und einer, der dem Client voraus ist, darf keinen Fehler werfen.
 */
export function isColossal(kindId: string): boolean {
  return balancing().enemies.find((enemy) => enemy.id === kindId)?.colossal ?? false;
}

/** Ein Gegner, wie ihn beide Seiten unabhaengig ausrechnen. */
export interface Hostile {
  id: string;
  kindId: string;
  x: number;
  y: number;
  r: number;
  maxHp: number;
  damage: number;
  reach: number;
  speed: number;
  xp: number;
  yieldMul: number;
  zoneIdx: number;
  /** Laufender Zustand — nicht Teil der Erzeugung. */
  hp: number;
  vx: number;
  vy: number;
  homeX: number;
  homeY: number;
  angle: number;
  /** Sekunden bis zum naechsten sichtbaren Schuss. **Nur Darstellung.** */
  shotT: number;
  /**
   * Feldzeit des letzten Treffers. **Nur Darstellung**, wie `shotT`.
   *
   * Der Lebensbalken blendet danach aus. Der Zeitpunkt entsteht im
   * **mutierenden** Pfad — `damage` solo, `applyServerDelta` online — nie beim
   * Zeichnen (Gate FG-17). `-Infinity` heisst: noch nie getroffen.
   */
  hitAt: number;
}

/**
 * Ein sichtbarer Gegnerschuss — **reine Darstellung**.
 *
 * Er traegt keinen Schaden: der ist stetig verrechnet, wie beim Drohnenanflug
 * und bei der Rakete. Ein Geschoss, das selbst buchte, waere eine zweite
 * Simulation neben der des Servers.
 */
export type HostileShotFn = (
  hostile: Hostile, zielX: number, zielY: number, speed: number,
) => void;

/**
 * Ein Gegner, den der **Wächter** weggeräumt hat — solo.
 *
 * Er braucht einen eigenen Rückruf, weil sein Abschuss `damage()` bewusst
 * umgeht: `onHostileDown` gibt Erz, Erfahrung und Zähler, und die darf hier
 * niemand bekommen (`GAME-001`). Ohne den Rückruf verschwände der Gegner
 * mitten im Bild — ohne Ton, ohne Funken, ohne irgendein Zeichen —, während
 * online dieselbe Lage über den `dead=true`-Delta hörbar und sichtbar ist.
 * Zwei verschiedene Spiele bei derselben Handlung.
 */
export type HostileFelledFn = (hostile: Hostile) => void;

/**
 * Welche Arten hier und jetzt ueberhaupt erscheinen duerfen.
 *
 * Der Schwierigkeitsverlauf steht in der **Tabelle**, nicht hier: in Zone 0
 * gibt es nur Schrottdrohnen, Jaeger und Schwarmmutter erst weiter draussen
 * und nach entsprechendem Ausbau.
 *
 * Die **Aera** kommt als dritte Bedingung dazu und wirkt in beide Richtungen:
 * die Waechter der Ruinen erscheinen nie in Akt I, die Gegner von Akt I laufen
 * dort weiter. Ohne sie waere `minZone` zweideutig — beide Aeren haben eine
 * Zone 0. Wortgleiche Entsprechung von `world/hostiles.py:_eligible`.
 */
function eligible(zoneIdx: number, built: number, era: number): Enemy[] {
  return balancing().enemies.filter(
    (enemy) => !enemy.colossal
      && enemy.minEra <= era
      && enemy.minZone <= zoneIdx
      && enemy.minBuild <= built,
  );
}

/** Die Gegner eines Chunks. **Die Reihenfolge der Ziehungen ist Vertrag.** */
/**
 * Der Ring, in dem der Wächter steht — Spiegel von `guardian_band`.
 *
 * Als Anteil der **Fundstellen-Kette** gerechnet: er steht hinter ihrer Mitte,
 * also findet ihn, wer der Spur schon ein Stück gefolgt ist.
 */
export function guardianBand(era: number): [number, number] {
  const zonen = zonesFor(era);
  const inner = zonen[zonen.length - 1].from;
  const tiefe = Math.max(1, balancing().caches.length) * balancing().cache.step;
  const [lo, hi] = balancing().guardian.band;
  return [inner + tiefe * lo, inner + tiefe * hi];
}

/**
 * Der Ort des Wächters. **Die Reihenfolge der Ziehungen ist Vertrag.**
 *
 * Eine eigene Folge, die keine Kachel berührt — sie verschiebt damit keine
 * Erzziehung (Gates FG-20, FG-30). Beide Seiten müssen denselben Punkt treffen,
 * sonst schießt der Spieler auf einen Gegner, den der Server woanders führt.
 */
export function guardianPoint(seed: number, era: number, out: { x: number; y: number }): void {
  const salt = balancing().guardian.salt;
  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(salt, 0, welt), salt, welt));

  const angle = rng() * TAU;                                            // 1
  const span = rng();                                                   // 2

  const [inner, outer] = guardianBand(era);
  const spread = balancing().guardian.spread;
  const mitte = (1 - spread) * 0.5;
  const radius = inner + (outer - inner) * (mitte + spread * span);
  out.x = jsRound(Math.cos(angle) * radius);
  out.y = jsRound(Math.sin(angle) * radius);
}

const wachpunkt = { x: 0, y: 0 };
const kachelpunkt = { x: 0, y: 0 };
/** Zielpuffer des Verfolgungsschritts — je Bild wiederverwendet (Gate FG-14). */
const gleitziel = { x: 0, y: 0 };

/**
 * Sein Schlüssel — **aus dem Saatpunkt**, nie aus dem Standort.
 *
 * Die Kachel im Namen ist seine **Heimat**kachel. Das ist der ganze Punkt: eine
 * ID, die mit ihm wandert, legte an jeder Kachelgrenze einen neuen Eintrag an —
 * der Kampf begänne mitten im Gefecht bei voller Hülle von vorn, und `live`
 * führte ihn doppelt. Nichts davon scheitert laut. Spiegel von `guardian_id`.
 */
export function guardianId(seed: number, era: number): string {
  guardianPoint(seed, era, kachelpunkt);
  const chunk = balancing().world.chunk;
  return `g:${Math.floor(kachelpunkt.x / chunk)}:${Math.floor(kachelpunkt.y / chunk)}:0`;
}

/**
 * Ein Schritt der Verfolgung: gleiten, dann an die Leine klemmen.
 *
 * **Gleichförmig und nicht exponentiell** — wortgleich zu `glide` in
 * `drones.ts` und aus demselben Grund: eine konstante Geschwindigkeit ist
 * schrittweiten-unabhängig, und damit kommen Client (60 Hz) und Server (5 Hz)
 * zur selben Zeit an. Ein exponentielles Glätten ist der naheliegendste
 * Verschönerungsgriff und ließe beide Seiten genau dort auseinanderlaufen, wo
 * niemand hinsieht; der Golden Vector fängt es deshalb ausdrücklich.
 *
 * `hover` ist der Abstand, den er vor dem Ziel hält — beim Verfolgen seine Kante
 * plus `enemyKeepDistance`, beim Heimweg 0, damit er **exakt** ankommt.
 *
 * **Die Leine klemmt das Ziel, nicht die Position** — und das ist keine
 * Geschmacksfrage, sondern die zweite Hälfte der Schrittweiten-Unabhängigkeit.
 * Die erste Fassung lief erst und zog dann radial zurück; das ist
 * **pfadabhängig**: ein grober Schritt landet weiter draußen und wird stärker
 * zurückgezogen, viele feine kriechen an der Leine entlang. Der Golden Vector
 * hat es sofort gefangen. Auf einen bereits geklemmten Zielpunkt zu ist die
 * Bewegung rein linear, und die Kreisscheibe ist konvex: wer innerhalb startet
 * und auf einen Punkt innerhalb zuläuft, bleibt innerhalb.
 *
 * Schreibt in ein übergebenes Ziel (Gate FG-14). Spiegel von `guardian_chase`.
 */
export function guardianChase(
  x: number, y: number, homeX: number, homeY: number,
  goalX: number, goalY: number, dt: number, hover: number,
  out: { x: number; y: number },
): void {
  onLeash(goalX, goalY, homeX, homeY, out);
  const dx = out.x - x;
  const dy = out.y - y;
  const dist = Math.hypot(dx, dy);
  const schritt = Math.min(dist - hover, balancing().guardian.speed * dt);
  if (dist > 1e-9 && schritt > 0) {
    x += (dx / dist) * schritt;
    y += (dy / dist) * schritt;
  }
  onLeash(x, y, homeX, homeY, out);
}

/** Einen Punkt radial in den Leinenkreis holen. */
function onLeash(
  x: number, y: number, homeX: number, homeY: number, out: { x: number; y: number },
): void {
  const dx = x - homeX;
  const dy = y - homeY;
  const weit = Math.hypot(dx, dy);
  const leash = balancing().guardian.leash;
  if (weit <= leash) {
    out.x = x;
    out.y = y;
    return;
  }
  out.x = homeX + (dx / weit) * leash;
  out.y = homeY + (dy / weit) * leash;
}

/**
 * Die Peilnotiz auf den Wächter — **aus seinem Ort gerechnet, nie umgekehrt.**
 *
 * Dieselbe tragende Regel wie bei `cacheHint`: stünde der Text in der Tabelle
 * und der Ort würde daraus gezogen, wären beide zwei Wahrheiten, und der Text
 * wäre die, die still lügt.
 *
 * Er beschreibt **`guardianPoint`, also sein Zuhause** — nicht die laufende
 * Position, und das ist eine Entscheidung und keine Näherung:
 *
 * * Die Richtung ist auf ein Achtel gerundet. ±22,5° sind bei 27.000 m rund
 *   ±10.600 m Bogen; eine Leine von 2.400 verschwindet darin restlos.
 * * Das Fundbuch ist eine **Notiz, die zwei Sitzungen überlebt**. Wo er gerade
 *   steht, hängt daran, wer gerade wo fliegt, und ist morgen ein anderer Ort.
 * * Der laufende Ort läge im `HostileField`, und das kennt `ui/` nicht (FG-04).
 *
 * Der Text daneben macht das Wandern zur Information statt zum Fehler.
 *
 * Sie lebt nur im Client: der Server braucht sie nicht, und deutscher
 * Anzeigetext zweimal wäre zweimal zu pflegen.
 */
export function guardianHint(seed: number, era: number): string {
  guardianPoint(seed, era, wachpunkt);
  return hintOf(wachpunkt.x, wachpunkt.y);
}

/**
 * Wen er verfolgt: das **nächstgelegene** Schiff in `guardian.aggro`.
 *
 * Reine Geometrie, gemessen von seinem **aktuellen** Ort — damit rechnen beide
 * Seiten dieselbe Antwort, ohne dass irgendwo eine Absprache nötig wäre.
 *
 * Der Tie-Breaker steht ausdrücklich da: bei gleichem Abstand entscheidet die
 * kleinere Koordinate, nicht die Reihenfolge der Liste. Sonst entschiede die
 * **Ladereihenfolge** — und die ist auf beiden Seiten eine andere, wie bei der
 * Drohnen-Zielordnung. Spiegel von `guardian_goal`.
 */
export function guardianGoal(
  x: number, y: number, candidates: readonly { x: number; y: number }[],
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestSq = 0;
  let bestX = 0;
  let bestY = 0;
  const aggro = balancing().guardian.aggro;
  const limit = aggro * aggro;
  for (const kandidat of candidates) {
    const dSq = (kandidat.x - x) ** 2 + (kandidat.y - y) ** 2;
    if (dSq > limit) continue;
    if (best === null || dSq < bestSq
      || (dSq === bestSq && (kandidat.x < bestX || (kandidat.x === bestX && kandidat.y < bestY)))) {
      best = kandidat;
      bestSq = dSq;
      bestX = kandidat.x;
      bestY = kandidat.y;
    }
  }
  return best;
}

/**
 * Ein vollständiger Schritt: Ziel wählen, gleiten, klemmen.
 *
 * **Die eine Regel**, die Server und Client teilen — der Server mit allen
 * Schiffen des Feldes, der Client solo mit dem eigenen. Ohne Ziel geht er heim,
 * und dort ist `hover` 0, damit er exakt ankommt. Spiegel von `guardian_step`.
 */
export function guardianStep(
  x: number, y: number, homeX: number, homeY: number,
  candidates: readonly { x: number; y: number }[], dt: number, hover: number,
  out: { x: number; y: number },
): void {
  const goal = guardianGoal(x, y, candidates);
  if (goal === null) guardianChase(x, y, homeX, homeY, homeX, homeY, dt, 0, out);
  else guardianChase(x, y, homeX, homeY, goal.x, goal.y, dt, hover, out);
}

/** Der Abstand, den er vor seinem Ziel hält — seine Kante plus Sicherheit. */
export function guardianHover(): number {
  const kind = enemy(balancing().guardian.id);
  return (kind ? kind.r : 0) + balancing().world.enemyKeepDistance;
}

/**
 * Seine Hülle — **ohne** die Zonenhärte, als einzige Art im Spiel.
 *
 * Spiegel von `warden_max_hp` in `world/guardian.py`, und der eine
 * Einstiegspunkt für die Frage „wie zäh ist er". Ein zweites `kind.hp *
 * zone.hp` für ihn wäre in Akt II um Faktor 37,5 daneben, und zwar still: der
 * Client zeigte einen vollen Lebensbalken, während der Server längst
 * ausgeschüttet hat.
 *
 * Null, wenn es die Zeile nicht gibt — die Tabellen kommen vom Server, und
 * einer, der dem Client voraus ist, darf keinen Browser am Start hindern.
 */
export function wardenMaxHp(): number {
  return enemy(balancing().guardian.id)?.hp ?? 0;
}

/**
 * Der Wächter — an seinem Saatpunkt, oder dort, wo der Server ihn führt.
 *
 * **Aus `at` kommen nur `x` und `y`.** ID, Zone, Hülle und Erfahrung folgen
 * weiter aus dem Saatpunkt: liefe die Hülle mit der gelaufenen Position, änderte
 * ein Schritt über eine Zonengrenze mitten im Gefecht seine `maxHp`. Bei einer
 * Leine von 2400 gegen rund 15 000 Einheiten Zonenreserve kann das nie
 * eintreten; die Invariante steht trotzdem hier, weil sie eine Aussage über die
 * **Konstruktion** ist und nicht über die heutigen Zahlen.
 *
 * `homeX/homeY` bleiben immer der Saatpunkt — sie sind die Mitte der Leine und
 * die Stelle, die der Hinweis im Fundbuch nennt.
 */
export function guardianHostile(
  seed: number, era: number, built: number, at?: { x: number; y: number } | null,
): Hostile | null {
  const kind = enemy(balancing().guardian.id);
  if (!kind || kind.minEra > era || kind.minBuild > built) return null;

  guardianPoint(seed, era, wachpunkt);
  // Er steht im tiefen Raum **seiner** Ära — nicht in „Zone >= 6". Akt I und
  // Akt II haben verschieden viele Ringe, und eine feste Zahl wäre in einem der
  // beiden still falsch: in Akt II gibt es keine Zone 6, der Wächter verschwände
  // dort ersatzlos. Gemessen wird am **Zuhause**: seine Werte dürfen sich
  // unterwegs nicht ändern.
  const zonen = zonesFor(era);
  const zoneIdx = zoneAtDist(Math.hypot(wachpunkt.x, wachpunkt.y), era);
  if (zoneIdx !== zonen.length - 1) return null;

  const zone = zonen[zoneIdx];
  const hp = wardenMaxHp();
  const homeX = wachpunkt.x;
  const homeY = wachpunkt.y;
  return {
    // Eigener ID-Raum: einer je **Feld**, nicht einer je Kachel. Die Kachel darin
    // ist seine **Heimat** und wandert nicht mit (`guardianId`).
    id: guardianId(seed, era),
    kindId: kind.id,
    x: at ? at.x : homeX, y: at ? at.y : homeY,
    r: kind.r,
    maxHp: hp,
    damage: kind.damage,
    reach: kind.reach,
    speed: kind.speed,
    xp: jsRound(kind.xp * zone.xp),
    yieldMul: kind.yieldMul,
    zoneIdx,
    hp,
    vx: 0, vy: 0,
    homeX, homeY,
    angle: 0,
    shotT: 0,
    hitAt: NEVER,
  };
}

/**
 * Alle gewöhnlichen Gegner in der Reichweite des Wächters — **ohne ihn selbst**.
 *
 * Spiegel von `victims_in_reach` in `world/wardenfire.py`. Sie steht **hier**
 * und nicht dort drüben, weil sie `hostilesInChunk` braucht: der Import zurück
 * wäre ein Zyklus (Gate FG-05). Die Entscheidungsregeln, die ohne die
 * Gegnertabelle auskommen, liegen in `wardenfire.ts`.
 *
 * `built = 0` ist Absicht und keine Vereinfachung: ID und Ort sind ohne `built`
 * bereits vollständig bestimmt, weil `chunkHostiles` sie **vor** der
 * Eignungsprüfung zieht. Ein `built` von außen machte die Antwort
 * beobachterabhängig, und derselbe Wächter räumte für jeden Spieler etwas
 * anderes weg.
 *
 * `withGuardian = false`, sonst räumte er sich selbst ab — seine Heimatkachel
 * liegt per Konstruktion in seiner eigenen Reichweite.
 *
 * Sortiert nach Abstand, bei Gleichstand nach ID. Der Tie-Breaker steht
 * ausdrücklich da, weil sonst die **Ladereihenfolge** entscheidet, und die ist
 * auf beiden Seiten eine andere.
 */
export function victimsInReach(
  gx: number, gy: number, reach: number, seed: number,
  era: number = balancing().eraFirst,
): Hostile[] {
  const chunk = balancing().world.chunk;
  const cx0 = Math.floor((gx - reach) / chunk);
  const cy0 = Math.floor((gy - reach) / chunk);
  const cx1 = Math.floor((gx + reach) / chunk);
  const cy1 = Math.floor((gy + reach) / chunk);
  const limit = reach * reach;

  const out: Hostile[] = [];
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cy = cy0; cy <= cy1; cy++) {
      for (const hostile of hostilesInChunk(cx, cy, seed, 0, era, false)) {
        const dx = hostile.x - gx;
        const dy = hostile.y - gy;
        if (dx * dx + dy * dy <= limit) out.push(hostile);
      }
    }
  }

  out.sort((a, b) => {
    const da = (a.x - gx) ** 2 + (a.y - gy) ** 2;
    const db = (b.x - gx) ** 2 + (b.y - gy) ** 2;
    return da - db || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  });
  return out;
}

/**
 * Den Wächter anhängen, **wenn** seine Heimatkachel diese ist.
 *
 * Angehängt und nicht eingezogen: seine Folge ist eine eigene, also bleiben die
 * Gegner dieser Kachel Byte für Byte dieselben.
 */
function pushGuardian(
  out: Hostile[], cx: number, cy: number, seed: number, built: number, era: number,
): void {
  guardianPoint(seed, era, kachelpunkt);
  const chunk = balancing().world.chunk;
  if (Math.floor(kachelpunkt.x / chunk) !== cx) return;
  if (Math.floor(kachelpunkt.y / chunk) !== cy) return;
  const wache = guardianHostile(seed, era, built);
  if (wache) out.push(wache);
}

/**
 * Die Gegner eines Chunks — gezogene **und** der Wächter, falls er hier wohnt.
 *
 * Der Wächter steht **hinter** der Kachelziehung und nicht darin. Die Ziehung
 * beginnt mit einem Wurf gegen `enemyChunkChance` und bricht in vier von fünf
 * Kacheln sofort ab; hinge er darin, erschiene er nur, wenn seine Kachel diesen
 * Wurf zufällig besteht. Ein fester Punkt, der von einem Kachelwurf abhängt, ist
 * kein fester Punkt.
 *
 * Seit der Verfolgung heißt der Wächter-Zweig „seine **Heimat**kachel ist diese".
 * Wo er gerade wirklich steht, führt `HostileField` — deshalb schaltet
 * `withGuardian` ihn ab, wo ein Aufrufer ihn selbst führt. Der Standardwert
 * bleibt `true`, damit der Golden Vector Byte für Byte gleich bleibt.
 */
export function hostilesInChunk(
  cx: number,
  cy: number,
  seed: number,
  built = 0,
  era: number = balancing().eraFirst,
  withGuardian = true,
): Hostile[] {
  const out = chunkHostiles(cx, cy, seed, built, era);
  if (withGuardian) pushGuardian(out, cx, cy, seed, built, era);
  return out;
}

function chunkHostiles(
  cx: number,
  cy: number,
  seed: number,
  built: number,
  era: number,
): Hostile[] {
  const world = balancing().world;
  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(cx, cy, welt), world.enemySalt, welt));

  if (rng() >= world.enemyChunkChance) return [];                       // 1

  const count = 1 + Math.floor(rng() * world.enemyMaxPerChunk);         // 2
  const out: Hostile[] = [];
  for (let index = 0; index < count; index++) {
    const x = jsRound((cx + rng()) * world.chunk);                      // 3
    const y = jsRound((cy + rng()) * world.chunk);                      // 4
    const pick = rng();                                                 // 5

    // Um die Kapsel herum bleibt das Feld frei — auch von Gegnern.
    const dist = Math.hypot(x, y);
    if (dist < world.clearRadius * 2) continue;

    const zoneIdx = zoneAtDist(dist, era);
    const arten = eligible(zoneIdx, built, era);
    if (!arten.length) continue;

    const kind = arten[Math.min(arten.length - 1, Math.floor(pick * arten.length))];
    const zone = zonesFor(era)[zoneIdx];
    out.push({
      id: `e:${cx}:${cy}:${index}`,
      kindId: kind.id,
      x, y,
      r: kind.r,
      maxHp: jsRound(kind.hp * zone.hp),
      damage: kind.damage,
      reach: kind.reach,
      speed: kind.speed,
      xp: jsRound(kind.xp * zone.xp),
      yieldMul: kind.yieldMul,
      zoneIdx,
      hp: jsRound(kind.hp * zone.hp),
      vx: 0, vy: 0,
      homeX: x, homeY: y,
      angle: 0,
      shotT: 0,
      hitAt: NEVER,
    });
  }
  return out;
}

/**
 * Die Gegner im Umkreis — laufender Zustand, nicht neu erzeugt.
 *
 * Solo ist der Client seine eigene Autoritaet (`store.authoritative`), online
 * bucht der Server und schickt Abweichungen. Beide Wege benutzen **denselben**
 * Speicher: der Unterschied ist nur, wer `damage` ruft.
 */
export class HostileField {
  private readonly live = new Map<string, Hostile>();
  private readonly loaded = new Set<string>();
  /** Erledigte Gegner mit ihrem Rueckkehrzeitpunkt. */
  private readonly gone = new Map<string, number>();
  private time = 0;
  /** Die Ausbaustufe, mit der die geladenen Chunks gezogen wurden. */
  private built = -1;

  /**
   * Wohin der Wächter gerade gleitet.
   *
   * Solo ist das der Punkt, den `guardianStep` selbst gerechnet hat; online der
   * zuletzt vom Server gemeldete. Beide Male gleitet der Sprite mit **derselben**
   * `guardian.speed` darauf zu — deshalb bleibt der Rückstand online konstant
   * bei rund 30 Einheiten (`speed × Takt`) statt zu wachsen. Gegen `reach` 900
   * sind das 3,3 %, dieselbe Größenordnung wie `ARRIVE_SLACK` bei den Drohnen.
   */
  private guardianGoalPos: { x: number; y: number } | null = null;
  /** Wen der Wächter solo gerade greift — `''` heißt „niemanden". */
  private wardenVictimId = '';
  private wardenSince = 0;
  private wardenSteppedAt = -Infinity;
  private wardenSuppressors = 0;

  constructor(
    private seed: number,
    /**
     * Rechnet dieser Client selbst?
     *
     * **Solo** führt er den Wächter mit derselben Regel wie der Server; **online**
     * übernimmt er nur, was `guardianAt`/`guardianSnap` hereinreichen. Ohne diese
     * Weiche liefen zwei Fassungen nebeneinander, und der Client zöge ihn zu
     * sich, während der Server ihn zum Nächststehenden zieht — genau die zweite
     * Wahrheit, gegen die die ganze Bauform steht.
     */
    private authoritative = true,
    /** Die Ära des Feldes — wie bei `World`, nicht je Abfrage. */
    private era: number = balancing().eraFirst,
  ) {}

  reset(seed: number, authoritative = true, era: number = balancing().eraFirst): void {
    this.seed = seed;
    this.authoritative = authoritative;
    this.era = era;
    this.live.clear();
    this.loaded.clear();
    this.gone.clear();
    this.guardianGoalPos = null;
    this.time = 0;
    this.built = -1;
    // **Die Wächter-Buchführung gehört mit zurückgesetzt.** `this.time` fängt
    // wieder bei 0 an; ein stehengebliebenes `wardenSteppedAt` aus der vorigen
    // Sitzung macht die Differenz stark negativ, und der Takt-Riegel greift
    // dann so lange, wie die alte Sitzung gedauert hat. Nach 25 Minuten Akt I
    // räumte der Wächter in Akt II 25 Minuten lang keinen einzigen Gegner weg
    // — Etappe 3 wäre solo still wirkungslos, während der Server (frischer
    // `FieldOverlay`, `warden_aim = None`) sofort losfeuert.
    this.wardenVictimId = '';
    this.wardenSince = 0;
    this.wardenSteppedAt = -Infinity;
    this.wardenSuppressors = 0;
  }

  /**
   * Den gemeldeten Ort des Wächters **hart** setzen — für den Aufschluss.
   *
   * `null` heißt „am Saatpunkt". Der Aufschluss ist genau der Moment, in dem der
   * Client beliebig veraltet sein kann (Verbinden, Sektorwechsel, Abschleppen);
   * dorthin zu **gleiten** hieße, nach einem Tab-Wechsel sechzehn Sekunden lang
   * an der falschen Stelle zu stehen.
   */
  guardianSnap(pos: { x: number; y: number } | null): void {
    const wache = this.live.get(guardianId(this.seed, this.era));
    const ziel = pos ?? (wache ? { x: wache.homeX, y: wache.homeY } : null);
    this.guardianGoalPos = ziel && { x: ziel.x, y: ziel.y };
    if (wache && ziel) {
      wache.x = ziel.x;
      wache.y = ziel.y;
    }
  }

  /** Der laufend gemeldete Ort — er wird angeglitten, nicht gesetzt. */
  guardianAt(x: number, y: number): void {
    this.guardianGoalPos = { x, y };
  }

  all(): IterableIterator<Hostile> {
    return this.live.values();
  }

  byId(id: string): Hostile | undefined {
    return this.live.get(id);
  }

  /**
   * Chunks um das Schiff herum nachladen und Erledigte zurueckbringen.
   *
   * **Eine neue Ausbaustufe wirft die geladenen Chunks weg.** `built` geht in
   * die Ziehung ein (`eligible`), und der Server rechnet sie bei jedem Tick
   * frisch: behielte der Client seine alten Gegner, meinten beide Seiten unter
   * derselben ID verschiedene Arten mit verschiedener Huelle — und die
   * Abweichungen passten nicht mehr zu dem, was hier steht. Sechsmal je
   * Spielstand ist das ein billiger Neuaufbau.
   *
   * Die Nachwachszeiten (`gone`) bleiben: der Ausbau aendert die Art, nicht die
   * Tatsache, dass dort gerade nichts steht.
   *
   * **Und ein Kampf ueberlebt den Neuaufbau.** ID und Ort haengen nicht am
   * Ausbau — `chunkHostiles` zieht `x`, `y` und `pick` **vor** der
   * Eignungspruefung —, nur die **Art** kann wechseln. Wer unter derselben ID
   * dieselbe Art bleibt, ist derselbe Gegner und behaelt Huelle und
   * Trefferzeit; nur bei gewechselter Art steht dort wirklich ein anderer.
   * Ohne diese Uebernahme heilte ein gekaufter Ausbau jeden angeschlagenen
   * Gegner — und den Waechter mitten in einem Kampf, der eine halbe Stunde
   * dauert. Gebaut wird ueber das Panel, und das geht von ueberall.
   */
  update(dt: number, x: number, y: number, built: number): void {
    this.time += dt;
    const vorher = built === this.built ? null : this.dropChunks(built);
    const world = balancing().world;
    const [cx, cy] = [Math.floor(x / world.chunk), Math.floor(y / world.chunk)];
    const radius = world.loadRadius;

    for (let ix = cx - radius; ix <= cx + radius; ix++) {
      for (let iy = cy - radius; iy <= cy + radius; iy++) {
        const key = `${ix}:${iy}`;
        if (this.loaded.has(key)) continue;
        this.loaded.add(key);
        for (const hostile of hostilesInChunk(ix, iy, this.seed, built, this.era, false)) {
          if (this.live.has(hostile.id)) continue;
          this.carryOver(hostile, vorher);
          this.live.set(hostile.id, hostile);
        }
      }
    }
    this.ensureGuardian(built, vorher);

    for (const [id, at] of this.gone) {
      if (this.time < at) continue;
      this.gone.delete(id);
      const hostile = this.live.get(id);
      if (!hostile) continue;
      hostile.hp = hostile.maxHp;
      // Der Nachruecker ist unberuehrt — sonst haenge ihm der Balken des
      // Erledigten noch fuer einen Wimpernschlag an.
      hostile.hitAt = NEVER;
      // Und er steht wieder **zu Hause**. Was zurückkommt, kommt an seinen Platz
      // zurück; ein Wächter, der dort nachrückt, wo er gefallen ist, stünde
      // dauerhaft außerhalb seines Rings.
      if (hostile.kindId === balancing().guardian.id) {
        hostile.x = hostile.homeX;
        hostile.y = hostile.homeY;
        this.guardianGoalPos = null;
      }
    }
  }

  /**
   * Die geladenen Chunks wegwerfen und den laufenden Zustand zurueckgeben.
   *
   * Der Rueckgabewert ist das Gedaechtnis des Neuaufbaus: `carryOver` setzt
   * daraus wieder ein, was derselbe Gegner geblieben ist.
   */
  private dropChunks(built: number): Map<string, Hostile> {
    this.built = built;
    const vorher = new Map(this.live);
    this.live.clear();
    this.loaded.clear();
    return vorher;
  }

  /**
   * Huelle und Trefferzeit eines frisch gezogenen Gegners festlegen.
   *
   * Stand unter derselben ID schon **dieselbe Art**, ist es derselbe Gegner:
   * sein Kampfstand wird uebernommen. Sonst startet er mit voller Huelle — und
   * steht seine Nachwachszeit noch aus, ist er trotzdem erledigt, sonst
   * braechte ein Neuaufbau die Toten zurueck.
   */
  private carryOver(hostile: Hostile, vorher: Map<string, Hostile> | null): void {
    const alt = vorher?.get(hostile.id);
    if (alt && alt.kindId === hostile.kindId) {
      hostile.hp = alt.hp;
      hostile.hitAt = alt.hitAt;
      return;
    }
    const zurueck = this.gone.get(hostile.id);
    if (zurueck !== undefined && this.time < zurueck) hostile.hp = 0;
  }

  /**
   * Den Wächter anlegen, **unabhängig vom Laderadius**.
   *
   * Er hängt nicht mehr an seiner Heimatkachel: die Karte lädt Kacheln einmalig
   * und entfernt sie nie, also sähe ihn nie, wer sein Zuhause nicht besucht hat
   * — und genau das wird der Normalfall, sobald er verfolgt. Es gibt ihn einmal
   * je Feld, also genügt ein Eintrag nach ID.
   *
   * Er geht **durch dieselbe Uebernahme** wie die Gegner aus den Chunks. Er ist
   * aus deren Schleife ausgenommen (`withGuardian = false`) und waere sonst der
   * eine, den ein gekaufter Ausbau heilt — bei einem Kampf ueber eine halbe
   * Stunde und 29 Baumknoten dahinter der teuerste denkbare stille Fehler.
   */
  private ensureGuardian(built: number, vorher: Map<string, Hostile> | null = null): void {
    const id = guardianId(this.seed, this.era);
    if (this.live.has(id)) return;
    const wache = guardianHostile(this.seed, this.era, built, this.guardianGoalPos);
    if (!wache) return;
    this.carryOver(wache, vorher);
    this.live.set(id, wache);
  }

  alive(hostile: Hostile): boolean {
    return hostile.hp > 0;
  }

  /**
   * Sekunden seit dem letzten Treffer, `Infinity` bei einem unberuehrten
   * Gegner. Rein und ohne Nebenwirkung — der Zeichenpfad fragt nur.
   */
  hitAgeOf(hostile: Hostile): number {
    return this.time - hostile.hitAt;
  }

  /** Einen Gegner treffen. Gibt zurueck, ob er dabei erledigt wurde. */
  damage(hostile: Hostile, amount: number): boolean {
    if (hostile.hp <= 0) return false;
    hostile.hp -= amount;
    hostile.hitAt = this.time;
    if (hostile.hp > 0) return false;
    hostile.hp = 0;
    this.gone.set(hostile.id, this.time + HOSTILE_RESPAWN);
    return true;
  }

  /**
   * Eine Abweichung vom Server uebernehmen (online).
   *
   * @param respawnIn Restzeit bis zur Rueckkehr. `null` heisst „gerade
   *   erledigt", also die volle Sperre. Der Aufschluss meldet die **Rest**zeit:
   *   mit der vollen bliebe ein Gegner beim Client tot, den der Server laengst
   *   wieder fuehrt — und der Spieler schoesse ins Leere, weil er ihn nicht
   *   sieht.
   * @param fresh ob die Abweichung ein frischer Treffer ist. Ein
   *   Bestandsabzug aus dem Aufschluss ist keiner, sonst leuchteten beim
   *   Verbinden alle Lebensbalken des Sektors auf einmal auf. Dieselbe
   *   Unterscheidung wie in `World.applyServerDelta`.
   */
  applyServerDelta(
    id: string, hp: number, dead: boolean,
    respawnIn: number | null = null, fresh = true,
  ): Hostile | undefined {
    const hostile = this.live.get(id);
    if (!hostile) return undefined;
    hostile.hp = dead ? 0 : hp;
    if (fresh) hostile.hitAt = this.time;
    if (dead) this.gone.set(id, this.time + (respawnIn ?? HOSTILE_RESPAWN));
    return hostile;
  }

  /**
   * Lebende Gegner, die dieser Kanal treffen darf — nach Distanz sortiert.
   *
   * **Wer schießen kann, kann getroffen werden.** Ein Gegner zählt auch dann,
   * wenn das Schiff in seinem Wachbereich steht, obwohl er außerhalb der
   * Waffenreichweite liegt: der Wachbereich reicht von 210 bis 780, der Laser
   * 95, die Drohnen 300. Jeder Gegner beschoss dich also, lange bevor du ihn
   * erreichtest — die Bündelung griff erst, wenn du fast auf ihm klebtest.
   *
   * Die Symmetrie kostet keine neue Balancing-Zahl: sie fällt aus Standort und
   * `reach`, auf beiden Seiten identisch rechenbar (`_targetable` im Server).
   *
   * `out` wird **wiederverwendet** — im Muster von `World.queryCircle`
   * (Gate FG-14).
   */
  inRange(x: number, y: number, radius: number, out: Hostile[]): Hostile[] {
    out.length = 0;
    for (const hostile of this.live.values()) {
      if (hostile.hp <= 0) continue;
      const dx = hostile.x - x;
      const dy = hostile.y - y;
      const distSq = dx * dx + dy * dy;
      if (distSq <= (radius + hostile.r) ** 2 || distSq <= hostile.reach ** 2) out.push(hostile);
    }
    if (out.length > 1) {
      out.sort((a, b) => (a.x - x) ** 2 + (a.y - y) ** 2 - ((b.x - x) ** 2 + (b.y - y) ** 2));
    }
    return out;
  }

  /**
   * Lebende Gegner in einem Kreis — **reine Geometrie**, ohne den Wachbereich.
   *
   * Der Unterschied zu `inRange` ist die Begründung, nicht die Formel. Dort
   * zählt der Wachbereich mit, weil das Schiff dort **steht** und beschossen
   * wird: „wer schießen kann, kann getroffen werden". Ein Kettensprung geht
   * dagegen von einem **wandernden Punkt** aus, an dem niemand steht — dort ist
   * `reach` keine Symmetrie mehr, sondern ein Leck. Mit `bounceRange` 130 und
   * einem `reach` bis 900 fing die Kette Gegner ein, die siebenmal weiter weg
   * standen als ihre Sprungweite, und der Server (`_targetable` misst vom
   * Schiff) buchte dort nichts.
   *
   * `out` wird **wiederverwendet** (Gate FG-14). Sortiert wird nicht: der eine
   * Aufrufer sucht das Nächste selbst und braucht keine Ordnung.
   */
  inCircle(x: number, y: number, radius: number, out: Hostile[]): Hostile[] {
    out.length = 0;
    for (const hostile of this.live.values()) {
      if (hostile.hp <= 0) continue;
      const dx = hostile.x - x;
      const dy = hostile.y - y;
      if (dx * dx + dy * dy <= (radius + hostile.r) ** 2) out.push(hostile);
    }
    return out;
  }

  /**
   * Gegner, in deren Wachbereich das Schiff steht — die, die gerade feuern.
   *
   * Für die Anzeige: der Spieler soll sehen, **dass** er im Gefecht steht und
   * damit auch, warum gerade nicht abgebaut wird. Die Antwort ist dieselbe wie
   * auf dem Server (`_engaged`), weil beide Seiten aus Standort und `reach`
   * rechnen — es braucht keine Nachricht dafür.
   */
  engaging(x: number, y: number, out: Hostile[]): Hostile[] {
    out.length = 0;
    for (const hostile of this.live.values()) {
      if (hostile.hp <= 0) continue;
      const dx = hostile.x - x;
      const dy = hostile.y - y;
      if (dx * dx + dy * dy <= hostile.reach ** 2) out.push(hostile);
    }
    return out;
  }

  /**
   * Gegner bewegen, ihre Schuesse melden und ihren Schaden aufsummieren.
   *
   * **Der Schaden haengt am Wachbereich, nicht an der gezeichneten Position.**
   * Ein Gegner trifft, solange das Schiff naeher als `reach` an seinem
   * **Standort** steht — wortgleich zu `_engaged` auf der Serverseite. Frueher
   * zaehlte hier die gelaufene Position; weil der Server keine Gegner bewegt,
   * waren Solo und Online damit zwei verschiedene Spiele, und online kam
   * praktisch nie Schaden an.
   *
   * Die Bewegung ist deshalb **Darstellung**: der Gegner kommt bis
   * `ENEMY_STANDOFF` in seinen Bereich hinein entgegen, bleibt darin und haelt
   * dabei `r + ENEMY_KEEP_DISTANCE` Abstand zum Schiff, statt auf ihm zu
   * parken. Auch die Schuesse sind Darstellung — sie melden nur, wann etwas
   * zu sehen ist.
   *
   * **Der Waechter ist die benannte Ausnahme, und er bricht die Regel nicht,
   * sondern erfuellt sie.** Sein Standort *ist* seine gelaufene Position, weil
   * ihn der **Server** fuehrt (`FieldOverlay.advance_guardian`) und dort
   * dieselbe Zahl in `_engaged` einsetzt. Der Satz oben lautete frueher „weil
   * der Server keine Gegner bewegt"; die Begruendung ist weggefallen, die Regel
   * gilt schaerfer als vorher — verboten ist eine Verfolgung, die **nur der
   * Client** rechnet. Sein Zweig steht in `stepGuardian`.
   *
   * Der Schaden wird **zurueckgegeben**, nicht gebucht: das Schiff schreibt den
   * Spielstand nicht (dieselbe Regel wie bei `Ship.update`).
   */
  step(
    dt: number, shipX: number, shipY: number, onShot?: HostileShotFn,
    onFelled?: HostileFelledFn,
  ): number {
    const standoff = balancing().world.enemyStandoff;
    const keep = balancing().world.enemyKeepDistance;
    let incoming = 0;
    const wacheId = balancing().guardian.id;
    for (const hostile of this.live.values()) {
      if (hostile.hp <= 0) continue;
      if (hostile.kindId === wacheId) {
        incoming += this.stepGuardian(hostile, dt, shipX, shipY, onShot, onFelled);
        continue;
      }
      const heimDx = shipX - hostile.homeX;
      const heimDy = shipY - hostile.homeY;
      const heimweg = Math.hypot(heimDx, heimDy) || 1;
      const engaged = heimweg <= hostile.reach;

      // Zielpunkt: dem Schiff entgegen, aber nie naeher als der Mindestabstand
      // und nie weiter als der Wachbereich. Steht das Schiff schon naeher am
      // Standort, bleibt der Gegner zu Hause — er weicht nicht rueckwaerts aus.
      const naeherung = Math.max(0, heimweg - (hostile.r + keep));
      const vorstoss = Math.min(naeherung, hostile.reach * standoff);
      const zielX = hostile.homeX + (heimDx / heimweg) * (engaged ? vorstoss : 0);
      const zielY = hostile.homeY + (heimDy / heimweg) * (engaged ? vorstoss : 0);
      this.drift(hostile, zielX, zielY, dt);

      if (!engaged) { hostile.shotT = 0; continue; }
      incoming += hostile.damage;
      hostile.angle = Math.atan2(shipY - hostile.y, shipX - hostile.x);
      this.fire(hostile, dt, shipX, shipY, onShot);
    }
    return incoming;
  }

  /**
   * Den Wächter führen — der eine Gegner, dessen Ort **Zustand** ist.
   *
   * Zwei Fassungen, eine Regel:
   *
   * * **Solo** rechnet dieser Client sie selbst, mit demselben `guardianStep`
   *   wie der Server — nur mit einem Kandidaten statt allen Schiffen des Feldes.
   * * **Online** gleitet er auf den zuletzt gemeldeten Punkt zu. Nicht springen:
   *   der Server meldet fünfmal je Sekunde, der Client zeichnet sechzigmal, und
   *   ein Sprung je Meldung sähe aus wie eine schlechte Leitung. `hover` ist
   *   dabei 0 — der Server hat ihn schon eingehalten, ein zweites Mal Abstand
   *   halten hieße, dem gemeldeten Ort nie zu folgen.
   *
   * Und der **Wachbereich misst gegen seinen gelaufenen Ort**, nicht gegen das
   * Zuhause wie bei jeder anderen Art. Das ist keine Sonderlocke, sondern die
   * Symmetrie zum Server: dort baut `guardian_hostile` ihn ebenfalls mit dem
   * geführten Ort, und `_engaged` liest genau den. Gegen `homeX/homeY` gemessen
   * schösse er hier auf zweitausend Einheiten Entfernung, während der Server
   * nichts bucht.
   */
  private stepGuardian(
    hostile: Hostile, dt: number, shipX: number, shipY: number, onShot?: HostileShotFn,
    onFelled?: HostileFelledFn,
  ): number {
    if (this.authoritative) {
      guardianStep(
        hostile.x, hostile.y, hostile.homeX, hostile.homeY,
        [{ x: shipX, y: shipY }], dt, guardianHover(), gleitziel,
      );
      this.guardianGoalPos = { x: gleitziel.x, y: gleitziel.y };
    } else if (this.guardianGoalPos) {
      guardianChase(
        hostile.x, hostile.y, hostile.homeX, hostile.homeY,
        this.guardianGoalPos.x, this.guardianGoalPos.y, dt, 0, gleitziel,
      );
    } else {
      gleitziel.x = hostile.x;
      gleitziel.y = hostile.y;
    }
    hostile.x = gleitziel.x;
    hostile.y = gleitziel.y;

    // **Solo räumt er selbst auf.** Online tut es der Abweichungsspeicher
    // (`FieldOverlay.step_warden`) und meldet die Toten als ganz normale
    // Abweichung; hier gibt es niemanden, der das täte. Ohne diesen Zweig gäbe
    // es Etappe 3 nur online — derselbe Preis wie bei den Progressionsregeln,
    // und dieselbe Antwort: dieselben Testfälle auf beiden Seiten.
    if (this.authoritative) this.wardenFire(hostile, onFelled);

    const dx = shipX - hostile.x;
    const dy = shipY - hostile.y;
    if (dx * dx + dy * dy > hostile.reach * hostile.reach) {
      hostile.shotT = 0;
      return 0;
    }
    hostile.angle = Math.atan2(dy, dx);
    this.fire(hostile, dt, shipX, shipY, onShot);
    return hostile.damage;
  }

  /**
   * Solo: den Wächter auf die Gegner um ihn herum losgehen lassen.
   *
   * Wortgleiches Gegenstück zu `FieldOverlay.step_warden` — dieselbe
   * Opferliste, dieselbe Klebrigkeit, derselbe Takt. Die **Regel** steht in
   * `sim/wardenfire.ts`, hier steht nur der Zustand: wen er greift und seit
   * wann.
   *
   * **Er nimmt niemandem etwas.** Kein Erz, keine Erfahrung, kein Zähler — es
   * gibt keinen Spieler, dem der Abschuss gehörte, und „der Nächststehende
   * bekommt es" wäre eine erfundene Regel. Deshalb geht der Weg auch nicht
   * über `damage`, das den Rückruf `onHostileDown` auslöst, sondern setzt die
   * Hülle direkt und stempelt den Nachwuchs.
   *
   * Der Takt hängt an `this.time`, also an der Uhr des Feldes und nicht am
   * `dt` des Aufrufers — dieselbe Idempotenz wie serverseitig.
   */
  private wardenFire(wache: Hostile, onFelled?: HostileFelledFn): void {
    if (this.time - this.wardenSteppedAt < balancing().warden.stepSeconds) return;
    this.wardenSteppedAt = this.time;

    const lebende = victimsInReach(wache.x, wache.y, wache.reach, this.seed, this.era)
      .map((opfer) => this.live.get(opfer.id) ?? opfer)
      .filter((opfer) => this.alive(opfer));
    const opfer = pickVictim(lebende, this.wardenVictimId);
    this.wardenSuppressors = suppression(lebende.length);
    if (!opfer) {
      this.wardenVictimId = '';
      return;
    }

    // Ein Zielwechsel setzt die Brenndauer zurück: sie ist **sein** Schaden an
    // diesem Gegner, kein Fortschrittsbalken des Feldes.
    if (opfer.id !== this.wardenVictimId) {
      this.wardenVictimId = opfer.id;
      this.wardenSince = this.time;
      return;
    }
    if (this.time - this.wardenSince < balancing().warden.burnSeconds) return;

    const lebend = this.live.get(opfer.id);
    if (lebend) {
      lebend.hp = 0;
      lebend.hitAt = this.time;
      // Ton und Funken kommen über den Rückruf, nicht über `damage()`: der
      // löst `onHostileDown` aus, und das gäbe Erz, Erfahrung und Zähler für
      // einen Abschuss, der keinem Spieler gehört.
      onFelled?.(lebend);
    }
    this.gone.set(opfer.id, this.time + HOSTILE_RESPAWN);
    this.wardenVictimId = '';
    this.wardenSuppressors = suppression(lebende.length - 1);
  }

  /**
   * Wie viele Gegner den Wächter zuletzt beschäftigt haben.
   *
   * Ausschließlich für die **Heilung** (`healFactor`) — es gibt keinen Weg von
   * hier zu seiner Hülle, und das ist die ganze Zusage.
   */
  wardenPressure(): number {
    return this.wardenSuppressors;
  }

  /** Auf einen Zielpunkt zuhalten. Reine Darstellung. */
  private drift(hostile: Hostile, zielX: number, zielY: number, dt: number): void {
    const dx = zielX - hostile.x;
    const dy = zielY - hostile.y;
    const weg = Math.hypot(dx, dy);
    const wunschX = weg > 4 ? (dx / weg) * hostile.speed : 0;
    const wunschY = weg > 4 ? (dy / weg) * hostile.speed : 0;
    const glaettung = Math.min(1, dt * 2.4);
    hostile.vx += (wunschX - hostile.vx) * glaettung;
    hostile.vy += (wunschY - hostile.vy) * glaettung;
    hostile.x += hostile.vx * dt;
    hostile.y += hostile.vy * dt;
    if (weg > 4) hostile.angle = Math.atan2(dy, dx);
  }

  /**
   * Den Schusstakt fortschreiben und sichtbare Schuesse melden.
   *
   * Gedeckelt auf **einen** Schuss je Schritt: nach einem Tab-Wechsel wuerde
   * sonst eine ganze Salve auf einmal losfliegen, obwohl der Schaden laengst
   * stetig verrechnet ist.
   */
  private fire(
    hostile: Hostile, dt: number, shipX: number, shipY: number, onShot?: HostileShotFn,
  ): void {
    const kind = enemy(hostile.kindId);
    if (!kind || kind.shotRate <= 0) return;
    hostile.shotT -= dt;
    if (hostile.shotT > 0) return;
    hostile.shotT = 1 / kind.shotRate;
    onShot?.(hostile, shipX, shipY, kind.shotSpeed);
  }
}
